import type { Lesson } from '@court-sync/shared';
import { isCampFullPackageFromDaySelection } from '@court-sync/shared';
import type { CampBookingMode, GroupBookingMode } from '../../../store/publicBooking';

/** Camp daily mode: user picks one or more calendar days in the sessions step. */
export function needsPublicBookingSessionPicker(
  lesson: Lesson | null | undefined,
  campBookingMode: CampBookingMode | null,
  _groupBookingMode: GroupBookingMode | null
): boolean {
  if (!lesson) return false;
  if (lesson.lessonType === 'camp' && (campBookingMode ?? 'daily') === 'daily') {
    return true;
  }
  return false;
}

/** Group term lesson booked as a single drop-in (next session auto-selected). */
export function isGroupDropInPublicBooking(
  lesson: Lesson | null | undefined,
  groupBookingMode: GroupBookingMode | null
): boolean {
  return (
    lesson?.lessonType === 'group' &&
    lesson.sessionPrice != null &&
    (groupBookingMode ?? 'full') === 'session'
  );
}

/** Whether checkout creates one booking row per selected session date. */
export function isPerSessionPublicBooking(
  lesson: Lesson | null | undefined,
  campBookingMode: CampBookingMode | null,
  groupBookingMode: GroupBookingMode | null
): boolean {
  return (
    needsPublicBookingSessionPicker(lesson, campBookingMode, groupBookingMode) ||
    isGroupDropInPublicBooking(lesson, groupBookingMode)
  );
}

/** Group lessons with optional drop-in pricing and an active term. */
export function supportsGroupSessionBooking(
  lesson: Lesson | null | undefined,
  termIsActive: boolean | undefined
): boolean {
  return (
    lesson?.lessonType === 'group' &&
    lesson.sessionPrice != null &&
    termIsActive === true
  );
}

/**
 * Session dates passed to createSocialBooking. When every selectable camp day is chosen,
 * returns a single null entry so the backend books full_camp at campFullPrice.
 */
export function resolvePublicBookingSessionDatesToBook(
  lesson: Lesson | null | undefined,
  campBookingMode: CampBookingMode | null,
  groupBookingMode: GroupBookingMode | null,
  selectedSessionDates: string[],
  unavailableDates: string[] = []
): (string | null)[] {
  if (!isPerSessionPublicBooking(lesson, campBookingMode, groupBookingMode)) {
    return [null];
  }
  if (
    lesson?.lessonType === 'camp' &&
    isCampFullPackageFromDaySelection(lesson, selectedSessionDates, unavailableDates)
  ) {
    return [null];
  }
  return [...selectedSessionDates];
}
